import { useMemo, useRef, useState } from 'react';
import type { DemoScript, Fx, Level, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, OK, TYPE, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { SCATTER, ShipTop } from './art.js';

/**
 * NAVIGATOR · JOIN THE STARS — tap the stars in order; a line joins each one as you tap it, and
 * the course is drawn across the night.
 *
 * Round 1: five stars, every one numbered. Round 2: five, and the numbers fade six seconds in —
 * remember them (a wrong star brings them back for a moment). Round 3: six, and the numbers fade
 * after three seconds. Tapping a star out of turn is the mistake; a tap on empty sky or on a star
 * already joined does nothing. (A round with stray stars and only the first numbered, the old
 * round 3, is still in the rules; no round deals it since 6 Oct 2026.)
 */

export interface StarsLevel {
  stars: number;
  /** Stars off the course. */
  strays: number;
  /** How long the numbers show from the start, in ms. */
  numbersMs: number;
  /** Only the first star is numbered, and the course is drawn faintly. */
  outline: boolean;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const STARS_LEVELS: readonly StarsLevel[] = easier(
  [
    { stars: 5, strays: 0, numbersMs: Number.POSITIVE_INFINITY, outline: false },
    { stars: 6, strays: 0, numbersMs: 3_000, outline: false },
  ],
  // Halfway between numbers that never fade and numbers that fade after three seconds.
  { numbersMs: 6_000 },
);

/** After a wrong star, the numbers come back for this long. */
const HINT_MS = 2_000;
/** A wrong star shakes and rings this long. */
const WRONG_MS = 700;
/** A new leg of the course draws itself over this long. */
const LEG_MS = 220;

export interface Pt {
  x: number;
  y: number;
}

/** The stars are laid in this box and stretched to the board. */
export const SKY = { w: 280, h: 200, gap: 58, lineClear: 22, minTurn: 30 } as const;

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

/** How far `p` is from the segment a–b. */
export function toSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy || 1;
  const t = clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / len2, 0, 1);
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Do the segments a–b and c–d cross? */
export function crosses(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

/**
 * A course a guest can read: no two legs cross, no leg runs close past another star, and no turn
 * folds back on itself. Every star, course and stray, keeps a finger's width from the others.
 */
export function readable(course: readonly Pt[], strays: readonly Pt[]): boolean {
  const all = [...course, ...strays];
  for (let i = 0; i < all.length; i++) for (let j = i + 1; j < all.length; j++) if (dist(all[i]!, all[j]!) < SKY.gap) return false;
  for (let i = 0; i + 1 < course.length; i++) {
    const a = course[i]!;
    const b = course[i + 1]!;
    for (let j = i + 2; j + 1 < course.length; j++) if (crosses(a, b, course[j]!, course[j + 1]!)) return false;
    for (const p of all) if (p !== a && p !== b && toSegment(p, a, b) < SKY.lineClear) return false;
  }
  for (let i = 1; i + 1 < course.length; i++) {
    const a = course[i - 1]!;
    const b = course[i]!;
    const c = course[i + 1]!;
    const u = Math.atan2(a.y - b.y, a.x - b.x);
    const v = Math.atan2(c.y - b.y, c.x - b.x);
    let turn = Math.abs(u - v) * (180 / Math.PI);
    if (turn > 180) turn = 360 - turn;
    if (turn < SKY.minTurn) return false;
  }
  return true;
}

/** Hand-laid skies for the rare deal the random one can't make readable. */
export const FALLBACK_SKIES: Record<Level, { course: Pt[]; strays: Pt[] }> = {
  0: { course: [{ x: 30, y: 150 }, { x: 95, y: 90 }, { x: 160, y: 130 }, { x: 225, y: 60 }, { x: 250, y: 160 }], strays: [] },
  1: { course: [{ x: 30, y: 60 }, { x: 95, y: 130 }, { x: 160, y: 70 }, { x: 225, y: 150 }, { x: 255, y: 50 }], strays: [] },
  2: {
    course: [{ x: 20, y: 60 }, { x: 85, y: 20 }, { x: 150, y: 70 }, { x: 110, y: 150 }, { x: 190, y: 170 }, { x: 260, y: 110 }],
    strays: [],
  },
};

/** Deals a sky: the course in order, and the strays. Always readable. */
export function layStars(level: Level, rand: () => number): { course: Pt[]; strays: Pt[] } {
  const L = STARS_LEVELS[level]!;
  const n = L.stars + L.strays;
  for (let attempt = 0; attempt < 300; attempt++) {
    const pts: Pt[] = [];
    for (let tries = 0; pts.length < n && tries < 400; tries++) {
      const p = { x: Math.round(rand() * SKY.w), y: Math.round(rand() * SKY.h) };
      if (pts.every((o) => dist(p, o) >= SKY.gap)) pts.push(p);
    }
    if (pts.length < n) continue;
    // A walk to a near star each time, now and then the second nearest, makes a shape, not a zigzag.
    const left = [...pts];
    const course = [left.splice(Math.floor(rand() * left.length), 1)[0]!];
    while (course.length < L.stars) {
      const at = course[course.length - 1]!;
      left.sort((a, b) => dist(at, a) - dist(at, b));
      const pick = rand() < 0.7 || left.length < 2 ? 0 : 1;
      course.push(left.splice(pick, 1)[0]!);
    }
    if (readable(course, left)) return { course, strays: left };
  }
  return FALLBACK_SKIES[level];
}

/** The star under a finger at (x, y), within `reach`, or −1. */
export function starAt(stars: readonly Pt[], x: number, y: number, reach: number): number {
  let best = -1;
  let bestD = reach;
  stars.forEach((s, i) => {
    const d = Math.hypot(s.x - x, s.y - y);
    if (d <= bestD) {
      best = i;
      bestD = d;
    }
  });
  return best;
}

const W = BOARD.w;
const H = BOARD.h;
/** The sky's box on the board, in units: stars sit between these, above the horizon. */
const TOP = 40;
const SIDE = 30;
const SKY_H = 206;
/** A finger this close to a star is on it, in units. */
const REACH = 30;

/** A star of the dealt sky, where it sits on the board. */
export const onBoard = (p: Pt): Pt => ({ x: q(SIDE + (p.x / SKY.w) * (W - 2 * SIDE)), y: q(TOP + (p.y / SKY.h) * SKY_H) });

/** Everything the chart shows. */
export interface StarsView {
  /** The game clock, ms. */
  t: number;
  /** The stars on the board (units): the course in order first, then any strays. */
  p: readonly (readonly [x: number, y: number])[];
  /** How many of them are the course. */
  n: number;
  /** Stars joined so far. */
  j: number;
  /** The numbers show. */
  num: boolean;
  /** The numbers fade in this round (the status asks to remember them). */
  fade: boolean;
  /** Only the first star is numbered, and the course is drawn faintly. */
  line: boolean;
  /** The newest leg of the course, drawn this far (0–1). */
  lg: number;
  /** A star tapped out of turn: which, and how long ago (ms). */
  w: readonly [i: number, age: number] | null;
  /** The course is set: the ship sails it, this far (0–1). */
  sail: number | null;
  fx?: Fx | undefined;
}

/** The horizon along the bottom of the board, by era. */
function Horizon({ era, id }: { era: Era; id: string }) {
  const skin = ERA_SKIN[era];
  if (era === 'stars') {
    return (
      <g>
        <circle cx={W / 2} cy={H + 430} r={470} fill="#0a2448" stroke={skin.haze} strokeOpacity=".16" strokeWidth="9" />
        <circle cx={W / 2} cy={H + 430} r={470} fill="none" stroke={skin.haze} strokeOpacity=".55" strokeWidth="1.4" />
      </g>
    );
  }
  if (era === 'neon') {
    let d = '';
    for (let x = -4, i = 0; x < W; x += 24, i++) {
      const h = 22 + ((i * 29) % 34);
      d += `M${x} ${H} L${x} ${H - h} L${x + 20} ${H - h} L${x + 20} ${H} Z `;
    }
    return (
      <g>
        <path d={d} fill="#170b28" stroke={skin.ink} strokeOpacity=".3" />
        <path d={d} fill={`url(#${id}win)`} opacity=".8" />
      </g>
    );
  }
  if (era === 'debris') {
    return (
      <g>
        <path
          d={`M0 ${H} L0 ${H - 26} L${W * 0.14} ${H - 30} L${W * 0.18} ${H - 48} L${W * 0.22} ${H - 32} L${W * 0.45} ${H - 36} L${W * 0.5} ${H - 22} L${W * 0.7} ${H - 30} L${W * 0.74} ${H - 52} L${W * 0.77} ${H - 34} L${W} ${H - 28} L${W} ${H} Z`}
          fill="#1d0a0b"
          stroke={skin.haze}
          strokeOpacity=".35"
        />
        {[0.2, 0.52, 0.76].map((f) => (
          <circle key={f} cx={W * f} cy={H - 30} r="2.2" fill={skin.haze} opacity=".7" />
        ))}
      </g>
    );
  }
  if (era === 'dust') {
    return (
      <g>
        <path d={`M0 ${H - 30} Q ${W * 0.3} ${H - 48} ${W * 0.55} ${H - 32} T ${W} ${H - 36} L${W} ${H} L0 ${H} Z`} fill="#3a2410" opacity=".85" />
        <path d={`M0 ${H - 14} Q ${W * 0.35} ${H - 30} ${W * 0.65} ${H - 16} T ${W} ${H - 18} L${W} ${H} L0 ${H} Z`} fill="#241507" />
      </g>
    );
  }
  let d = `M0 ${H}`;
  for (let x = 0; x <= W + 16; x += 16) d += ` L${x} ${H - 24 - ((x * 13) % 18)} L${x + 8} ${H - 32 - ((x * 7) % 16)}`;
  d += ` L${W} ${H} Z`;
  return <path d={d} fill="#0c2410" stroke={skin.ink} strokeOpacity=".3" />;
}

/** The ship on a set course, `f` of the way along it (0–1): where it is and which way it points. */
function sailing(course: readonly (readonly [number, number])[], f: number): { x: number; y: number; r: number } | null {
  if (course.length < 2) return null;
  const lens = course.slice(1).map((p, k) => Math.hypot(p[0] - course[k]![0], p[1] - course[k]![1]));
  let d = clamp(f, 0, 1) * lens.reduce((a, b) => a + b, 0);
  for (let k = 0; k < lens.length; k++) {
    if (d <= lens[k]! || k === lens.length - 1) {
      const [ax, ay] = course[k]!;
      const [bx, by] = course[k + 1]!;
      const g = clamp(d / (lens[k]! || 1), 0, 1);
      return { x: ax + (bx - ax) * g, y: ay + (by - ay) * g, r: (Math.atan2(by - ay, bx - ax) * 180) / Math.PI + 90 };
    }
    d -= lens[k]!;
  }
  return null;
}

/** The chart, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<StarsView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const { p, n, t } = view;
  // A demo glides every number between its keys: counts are read rounded.
  const joined = Math.round(view.j);
  const wrong = view.w && view.w[1] < WRONG_MS ? view.w : null;
  const ship = view.sail === null ? null : sailing(p.slice(0, n), view.sail);
  const status =
    view.sail !== null
      ? { text: say(lang, '✓ COURSE SET', '✓ KURSEN ER LAGT'), tone: 'ok' as const }
      : wrong
        ? { text: say(lang, 'NOT THAT ONE', 'IKKE DEN'), tone: 'bad' as const }
        : {
            text: say(lang, 'STARS', 'STJERNER'),
            count: [joined, n] as const,
            hint: view.line
              ? say(lang, 'FOLLOW THE LINE FROM 1', 'FØLG LINJEN FRA 1')
              : view.fade && view.num && joined === 0
                ? say(lang, 'REMEMBER THE ORDER', 'HUSK REKKEFØLGEN')
                : say(lang, 'TAP IN ORDER', 'TRYKK I REKKEFØLGE'),
          };
  const wave = (period: number, phase = 0) => 0.5 + 0.5 * Math.sin((t / 1000) * ((2 * Math.PI) / period) + phase);

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <radialGradient id={`${id}halo`}>
          <stop offset="0" stopColor={skin.haze} stopOpacity=".55" />
          <stop offset="1" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}lit`}>
          <stop offset="0" stopColor={accent} stopOpacity=".6" />
          <stop offset="1" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}neb`}>
          <stop offset="0" stopColor={skin.ink} stopOpacity=".2" />
          <stop offset="1" stopColor={skin.ink} stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}win`} width="7" height="8" patternUnits="userSpaceOnUse">
          <rect x="2" y="2" width="2.4" height="2.4" fill={skin.haze} opacity=".5" />
        </pattern>
      </defs>

      {/* the chart: a nebula, faint arcs of the grid, and the small stars */}
      <ellipse cx={W * 0.68} cy={100} rx={170} ry={90} fill={`url(#${id}neb)`} />
      <circle cx={W * 0.5} cy={-260} r={400} fill="none" stroke={skin.haze} strokeOpacity=".08" />
      <circle cx={W * 0.5} cy={-260} r={480} fill="none" stroke={skin.haze} strokeOpacity=".06" />
      <path d={`M${W * 0.3} 0 Q ${W * 0.36} ${H * 0.5} ${W * 0.3} ${H}`} stroke={skin.haze} strokeOpacity=".06" fill="none" />
      <path d={`M${W * 0.7} 0 Q ${W * 0.64} ${H * 0.5} ${W * 0.7} ${H}`} stroke={skin.haze} strokeOpacity=".06" fill="none" />
      {SCATTER.map((s, i) => (
        <circle
          key={i}
          cx={s.x * W}
          cy={s.y * (H - 50)}
          r={s.r * 0.7}
          fill={INK.text}
          opacity={i % 4 === 0 ? 0.15 + 0.45 * wave(3 + (i % 3), s.d) : 0.25 + (i % 4) * 0.1}
        />
      ))}
      <Horizon era={era} id={id} />
      {/* the navigator's brackets */}
      {[
        [10, 10, 1, 1],
        [W - 10, 10, -1, 1],
        [10, H - 10, 1, -1],
        [W - 10, H - 10, -1, -1],
      ].map(([x, y, sx, sy]) => (
        <path key={`${x}-${y}`} d={`M${x!} ${y! + 14 * sy!} L${x!} ${y!} L${x! + 14 * sx!} ${y!}`} stroke={accent} strokeOpacity=".4" strokeWidth="1.5" fill="none" />
      ))}

      {/* a round that outlines the course: drawn faintly */}
      {view.line &&
        p.slice(1, n).map((s, k) => (
          <line key={k} x1={p[k]![0]} y1={p[k]![1]} x2={s[0]} y2={s[1]} stroke={skin.haze} strokeOpacity=".45" strokeWidth="1.6" strokeDasharray="3 5" />
        ))}

      {/* the course so far: each leg in full but the newest, which draws itself */}
      {p.slice(1, Math.min(joined, n)).map((s, k) => {
        const [ax, ay] = p[k]!;
        const f = k === joined - 2 ? clamp(view.lg, 0, 1) : 1;
        const x2 = ax + (s[0] - ax) * f;
        const y2 = ay + (s[1] - ay) * f;
        return (
          <g key={k}>
            <line x1={ax} y1={ay} x2={x2} y2={y2} stroke={accent} strokeOpacity=".2" strokeWidth="9" strokeLinecap="round" />
            <line x1={ax} y1={ay} x2={x2} y2={y2} stroke={accent} strokeWidth="2.6" strokeLinecap="round" />
          </g>
        );
      })}

      {p.map(([x, y], i) => {
        const done = i < n && i < joined;
        const shake = wrong && wrong[0] === i ? Math.sin(wrong[1] / 35) * 4 * (1 - wrong[1] / WRONG_MS) : 0;
        const numbered = i < n && (done || (view.line ? i === 0 : view.num));
        return (
          <g key={i} transform={`translate(${x + shake} ${y})`}>
            <circle r="21" fill={`url(#${done ? `${id}lit` : `${id}halo`})`} opacity={done ? 1 : 0.7 + 0.3 * wave(3.2 + (i % 3) * 0.6)} />
            <path d="M0 -11 Q1.3 -1.3 11 0 Q1.3 1.3 0 11 Q-1.3 1.3 -11 0 Q-1.3 -1.3 0 -11 Z" fill={done ? '#ffffff' : '#e8f1fa'} opacity=".92" />
            <circle r={done ? 4.4 : 3.6} fill="#ffffff" />
            {done && <circle r="13" fill="none" stroke={accent} strokeWidth="2" />}
            {view.line && i === 0 && !done && <circle r={13 + 4 * wave(2.2)} fill="none" stroke={accent} strokeWidth="1.6" opacity={0.9 - 0.55 * wave(2.2)} />}
            {wrong && wrong[0] === i && <circle r="15" fill="none" stroke={BAD} strokeWidth="2.2" opacity={1 - wrong[1] / WRONG_MS} />}
            {i < n && (
              <g transform="translate(15 -15)" style={{ opacity: numbered ? 1 : 0, transition: 'opacity .6s' }}>
                <circle r="9.5" fill={done ? accent : '#0b1220'} stroke={done ? accent : skin.haze} strokeWidth="1.2" />
                <text y="4.3" textAnchor="middle" fontSize={TYPE.label} fontWeight="700" fontFamily={mono} fill={done ? INK.onFill : INK.hi}>
                  {i + 1}
                </text>
              </g>
            )}
          </g>
        );
      })}

      {ship && (
        <g transform={`translate(${ship.x} ${ship.y}) rotate(${ship.r}) scale(.55)`}>
          <ShipTop era={era} burn={0.8} />
        </g>
      )}
      {view.sail !== null && view.sail >= 1 && <circle cx={p[n - 1]![0]} cy={p[n - 1]![1]} r="18" fill="none" stroke={OK} strokeWidth="2" />}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const L = STARS_LEVELS[level]!;
  const [sky] = useState(() => layStars(level, seeded(Date.now() + level * 104729)));
  /** The stars on the board, in units: the course, then the strays. */
  const pts = useMemo(() => [...sky.course, ...sky.strays].map(onBoard), [sky]);
  const n = L.stars;
  const next = useRef(0);
  const joinedAt = useRef(-1e9);
  const wrong = useRef<{ i: number; at: number } | null>(null);
  const numbersUntil = useRef<number | null>(null);

  const round = useRound(props, (_dt, now) => {
    if (numbersUntil.current === null) numbersUntil.current = now + L.numbersMs;
  });

  const tap = (at: { x: number; y: number }) => {
    if (!round.live) return;
    const i = starAt(pts, at.x, at.y, REACH);
    if (i < 0 || (i < n && i < next.current)) return;
    const now = round.time();
    if (i === next.current) {
      joinedAt.current = now;
      next.current += 1;
      round.hit();
      round.progress(next.current / n);
      if (next.current >= n) round.win();
      return;
    }
    // Out of turn, or a stray: the mistake. Where the numbers fade, they come back for a moment.
    wrong.current = { i, at: now };
    if (Number.isFinite(L.numbersMs)) numbersUntil.current = Math.max(numbersUntil.current ?? 0, now + HINT_MS);
    round.miss();
  };

  const now = round.now;
  const view: StarsView = {
    t: q(now),
    p: pts.map((s) => [s.x, s.y] as const),
    n,
    j: next.current,
    num: numbersUntil.current === null || now < numbersUntil.current,
    fade: Number.isFinite(L.numbersMs),
    line: L.outline,
    lg: q(clamp((now - joinedAt.current) / LEG_MS, 0, 1), 2),
    w: wrong.current && now - wrong.current.at < WRONG_MS ? [wrong.current.i, q(now - wrong.current.at)] : null,
    sail: round.won ? q(round.clear, 2) : null,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap the stars in order', 'Trykk på stjernene i rekkefølge'), down: tap }}
    />
  );
}

/** The how-to's sky: round 1's hand-laid one. */
const DEMO_SKY = FALLBACK_SKIES[0].course.map(onBoard);
const DEMO_TAPS = [900, 1_900, 2_900, 3_900, 4_900];

/**
 * The how-to: the finger taps star 1, 2, 3, 4 and 5 in turn; a line draws to each, and the ship
 * sails the finished course.
 */
const demo: DemoScript<StarsView> = {
  ms: 7_200,
  keys: [
    { at: 0, view: { t: 0, p: DEMO_SKY.map((s) => [s.x, s.y] as const), n: 5, j: 0, num: true, fade: false, line: false, lg: 1, w: null, sail: null } },
    ...DEMO_TAPS.flatMap((at, k) => [
      { at, view: { j: k + 1, lg: 0, fx: [0.8, 0] as const }, ease: 'step' as const },
      { at: at + LEG_MS, view: { lg: 1 } },
      { at: at + 450, view: { fx: [0, 0] as const } },
    ]),
    { at: 5_400, view: { sail: 0 }, ease: 'step' },
    { at: 6_400, view: { sail: 1 }, ease: 'inOut' },
  ],
  finger: [
    { at: 0, x: 290, y: 300, hide: true },
    { at: 300, x: 290, y: 300, hide: false },
    ...DEMO_TAPS.flatMap((at, k) => [
      { at: at - 350, x: DEMO_SKY[k]!.x, y: DEMO_SKY[k]!.y },
      { at, x: DEMO_SKY[k]!.x, y: DEMO_SKY[k]!.y, down: true },
      { at: at + 160, x: DEMO_SKY[k]!.x, y: DEMO_SKY[k]!.y, down: false },
    ]),
    { at: 5_600, x: 290, y: 300, hide: true },
  ],
  steps: [
    { at: 0, en: 'TAP STAR 1, THEN 2, THEN 3', no: 'TRYKK STJERNE 1, SÅ 2, SÅ 3' },
    { at: 2_400, en: 'A LINE JOINS EACH STAR', no: 'EN STREK GÅR TIL HVER' },
    { at: 5_000, en: 'LATER, REMEMBER THE NUMBERS', no: 'SENERE: HUSK TALLENE' },
  ],
};

export const game = thirtyGame<StarsView>({
  Board,
  View,
  demo,
  words: {
    en: {
      instruction: 'Tap the stars in order to draw the course. Later rounds: remember the numbers.',
      howMove: 'Tap star 1, then 2, then 3. A line joins each star you tap.',
    },
    no: {
      instruction: 'Trykk på stjernene i rekkefølge. Senere runder: husk tallene.',
      howMove: 'Trykk på stjerne 1, så 2, så 3. En strek går til hver stjerne du trykker på.',
    },
  },
});
